import React, { useState, useRef, useEffect } from "react";
import { toast } from "react-toastify";
import textToSpeech from "../../utils/textToSpeech";
import { sanitizeTextInput, copyRichTextToClipboard } from "../../utils/textUtils";
import useOutsideClick from "../../hooks/useOutsideClick";
import { useConstants } from "../../constant/constantProvider";
import { exportToPDF, exportToDOCX } from "../../utils/exportUtils";
import { getExportFormattedDate } from "../../utils/dateUtils";
import "./FeedbackButtons.css";
import { getObjectUrlFromBase64, downloadImage } from "../../utils/fileHelper";
import GenericTooltip from "../GenericTooltip/GenericTooltip";

// Icons
import like from "../../assets/new-images/like-icon.svg";
import dislike from "../../assets/new-images/dislike-icon.svg";
import likeSelected from "../../assets/new-images/active-like-icon.svg";
import dislikeSelected from "../../assets/new-images/active-dislike-icon.svg";
import copyInactive from "../../assets/new-images/copy-icon.svg";
import copyActive from "../../assets/new-images/active-copy-icon.svg";
import speakerIcon from "../../assets/new-images/play-audio-speaker-icon.svg";
import pauseIcon from "../../assets/new-images/active-pause-audio-icon.svg";
import regenerateIcon from "../../assets/new-images/regenerate-icon.svg";
import activeRegenerateIcon from "../../assets/new-images/active-regenerate-icon.svg";

import selectedIcon from "../../assets/images/check-icon.svg";
import downloadIcon from "../../assets/images/download-icon.svg";

import closeIcon from "../../assets/new-images/outline-close-icon.svg";
import pdfIcon from "../../assets/new-images/pdf-icon.svg";
import wordIcon from "../../assets/new-images/docs-icon.svg";
import threeDotsIcon from "../../assets/images/three-dots-icon.svg";

const FeedbackButtons = ({
  show = [], // Array of button types to show: ["like", "dislike", "regenerate", "copy", "audio", "download"]
  pair, // The message pair object
  index, // Index of the message in conversation
  getTextElement, // Function to get the text element: () => HTMLElement | null
  pairId, // ID of the pair (for copy functionality)
  feedback, // Current feedback state ("like" or "dislike")
  isLoading, // Whether conversation is loading
  isLastMessage, // Whether this is the last message
  // Callbacks
  onFeedback, // (index, type, pair) => void
  onRegenerate, // (index, option) => void
  onDownload, // (pair) => void
  // Optional props
  models = [], // Models for regeneration dropdown
  imageData = null, // Image data for download button
  procurementTableData = null, // Procurement table data
  exportResponse = [], // Array of export formats to show: ["pdf", "docx"]
  moreOption = [], // Array of legacy/custom buttons inside kebab menu
  thinkingEnabled = false, // Whether thinking mode is active
  isImageGeneration = false, // Whether this is an image generation session
}) => {
  const {
    REGENERATE_WITH,
    EXPORTING_TO_PDF,
    EXPORTING_TO_DOCX,
    EXPORT_CONTENT_NOT_FOUND,
    FAILED_TO_EXPORT_PDF,
    FAILED_TO_EXPORT_DOCX,
    PRESS_ENTER_TO_SUBMIT,
    LIKE,
    DISLIKE,
    COPY,
    PLAY_AUDIO,
    STOP_AUDIO,
    DOWNLOAD_IMAGE,
    REGENERATE,
    MORE_OPTIONS,
    EXPORT_AS_PDF,
    EXPORT_AS_DOCX,
    YOUR_FEEDBACK,
    FAILED_TO_COPY,
    TEXT_ELEMENT_NOT_FOUND,
    COPY_SUCCESS
  } = useConstants();
  // Audio state (fully independent)
  const [currentAudioId, setCurrentAudioId] = useState(null);
  const [audioLoadingId, setAudioLoadingId] = useState(null);

  // Copy state
  const [copied, setCopied] = useState(false);

  // Export state
  const [exportingPDF, setExportingPDF] = useState(false);
  const [exportingDOCX, setExportingDOCX] = useState(false);

  // Feedback input state
  const [showFeedbackInput, setShowFeedbackInput] = useState(false);
  const [feedbackInput, setFeedbackInput] = useState("");
  const [isTypingFeedback, setIsTypingFeedback] = useState(false);
  const feedbackInputRef = useRef(null);
  const feedbackContainerRef = useRef(null);

  // Regenerate modal state
  const [isRegenerateOptionModalOpen, setRegenerateOptionModalOpen] = useState(false);
  const [generateModalPosition, setGenerateModalPosition] = useState("bottom");
  const generateDropdownRef = useRef(null);
  const triggerGenerateRef = useRef(null);
  const generateModalRef = useRef(null);

  // Kebab menu state
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState("bottom");
  const menuDropdownRef = useRef(null);
  const menuTriggerRef = useRef(null);

  useOutsideClick(generateDropdownRef, () => setRegenerateOptionModalOpen(false));
  useOutsideClick(menuDropdownRef, () => setIsMenuOpen(false));

  const combinedMoreOptions = React.useMemo(() => {
    return Array.from(new Set([...exportResponse, ...moreOption]));
  }, [exportResponse, moreOption]);

  const getExportElement = () => {
    return getTextElement
      ? getTextElement()
      : document.querySelector(`[data-pair-id="${pairId}"]`);
  };

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      textToSpeech.stop();
      setCurrentAudioId(null);
      setAudioLoadingId(null);
    };
  }, []);

  // Handle audio play (fully independent)
  const handlePlayAudio = (pair) => {
    const id = pair?.id;

    // Prevent multiple clicks
    if (audioLoadingId === id) return;

    // Stop if clicked on the same one currently playing
    if (currentAudioId === id) {
      textToSpeech.stop();
      setCurrentAudioId(null);
      setAudioLoadingId(null);
      return;
    }

    // Stop any currently playing speech
    textToSpeech.stop();
    setCurrentAudioId(null);
    setAudioLoadingId(id);

    const messageText = pair?.content || "";

    // Use the utility to speak the text
    const success = textToSpeech.speak(messageText, {
      onStart: () => {
        setCurrentAudioId(id);
        setAudioLoadingId(null);
      },
      onEnd: () => {
        setCurrentAudioId(null);
        setAudioLoadingId(null);
      },
      onError: (error) => {
        setCurrentAudioId(null);
        setAudioLoadingId(null);
        console.error("Text-to-speech error:", error.message);
      },
    });

    // If speech failed to start, reset loading state
    if (!success) {
      setAudioLoadingId(null);
    }
  };

  // Handle copy click
  const handleCopyClick = async () => {
    const el = getExportElement();

    if (!el) {
      toast.error(TEXT_ELEMENT_NOT_FOUND);
      return;
    }

    handleCopyFromElement(el);
  };

  const handleCopyFromElement = async (el) => {
    try {
      const clone = el.cloneNode(true);
      const copyButtons = clone.querySelectorAll('.markdown-copy-button');
      copyButtons.forEach(btn => btn.remove());

      const htmlContent = clone.outerHTML;
      let plainText = clone.innerText || clone.textContent;

      // Clean up excessive whitespace: collapse 3+ newlines into 2, and trim
      if (plainText) {
        plainText = plainText.replace(/(\n\s*){3,}/g, '\n\n').trim();
      }

      const success = await copyRichTextToClipboard(plainText, htmlContent);

      if (success) {
        setCopied(true);
        toast.info(COPY_SUCCESS, { autoClose: 1000 });
        setTimeout(() => {
          setCopied(false);
        }, 3000);
      } else {
        throw new Error("Clipboard copy failed");
      }
    } catch (err) {
      console.error("Failed to copy:", err);
      toast.error(FAILED_TO_COPY);
    }
  };

  // Handle feedback click
  const handleFeedbackClick = (type) => {
    if (onFeedback) {
      // Pass empty comment for initial feedback
      onFeedback(index, type, pair, "");
    }
    setShowFeedbackInput(type === "dislike");

    // Focus feedback input if dislike
    if (type === "dislike") {
      setTimeout(() => {
        if (feedbackInputRef.current && feedbackContainerRef.current?.offsetParent !== null) {
          feedbackInputRef.current.focus();
          feedbackContainerRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 0);
    }
  };

  // Handle feedback modal close
  const handleFeedbackModalClose = () => {
    setShowFeedbackInput(false);
    setFeedbackInput("");
  };

  // Handle feedback comment change
  const handleFeedbackCommentChange = (event) => {
    const updatedComment = sanitizeTextInput(event.target.value);
    setFeedbackInput(updatedComment);
    setIsTypingFeedback(updatedComment.trim().length > 0);
  };

  // Handle key press in feedback input
  const handleKeyPressFeedback = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      const comment = feedbackInput.trim();
      handleFeedbackModalClose();
      if (onFeedback) {
        onFeedback(index, "dislike", pair, comment);
      }
    } else if (event.key === "Enter" && event.shiftKey) {
      // Allow Shift+Enter for new line
      event.preventDefault();
      setFeedbackInput((prev) => prev + "\n");
    }
  };

  // Handle regenerate modal open
  const openSelectRegenerationModal = () => {
    if (!models || models.length === 0) {
      if (onRegenerate) {
        onRegenerate(index, null);
      }
      return;
    }

    const willOpen = !isRegenerateOptionModalOpen;

    if (willOpen && triggerGenerateRef.current) {
      // Calculate position before opening
      const rect = triggerGenerateRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const remainingHeight = viewportHeight - rect.bottom;

      // If less than 200px remaining below, show modal on top
      setGenerateModalPosition(remainingHeight < 350 ? "top" : "bottom");
    }

    setRegenerateOptionModalOpen(willOpen);
  };

  // Handle kebab menu opening
  const openMenuModal = () => {
    const willOpen = !isMenuOpen;

    if (willOpen && menuTriggerRef.current) {
      const rect = menuTriggerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const remainingHeight = viewportHeight - rect.bottom;
      setMenuPosition(remainingHeight < 350 ? "top" : "bottom");
    }

    setIsMenuOpen(willOpen);
  };

  // Handle model selection for regeneration
  const handleOutputModalGenerationSelect = (option) => {
    setRegenerateOptionModalOpen(false);
    if (onRegenerate) {
      onRegenerate(index, option);
    }
  };

  // Handle download
  const handleDownload = () => {
    if (imageData?.base64_image) {
      downloadImage(
        getObjectUrlFromBase64(imageData.base64_image),
        `${imageData.image_id || "image"}.png`
      );
    } else if (onDownload) {
      onDownload(pair);
    }
  };

  // Don't render if no buttons to show
  if (!show || show.length === 0) {
    return <div className="feedback-buttons" />;
  }

  const shouldShowLike = show.includes("like");
  const shouldShowDislike = show.includes("dislike");
  const shouldShowCopy = show.includes("copy") && !isImageGeneration;
  const shouldShowAudioOutside = show.includes("audio");
  const shouldShowAudioInside = combinedMoreOptions.includes("audio");
  const shouldShowRegenerate = show.includes("regenerate");
  const shouldShowDownload = show.includes("download");
  const shouldShowExportPDF = combinedMoreOptions.includes("pdf");
  const shouldShowExportDOCX = combinedMoreOptions.includes("docx");
  const shouldShowKebabMenu = (shouldShowAudioInside || shouldShowExportPDF || shouldShowExportDOCX) && !isImageGeneration;

  const getFileName = () => {
    return `Chat_Genie_Response_${getExportFormattedDate()}`;
  };

  // Handle PDF export
  const handleExportPDF = async () => {
    const el = getExportElement();
    if (!el) {
      toast.error(EXPORT_CONTENT_NOT_FOUND);
      return;
    }
    setExportingPDF(true);
    toast.info(EXPORTING_TO_PDF);
    try {
      await exportToPDF(el, getFileName());
    } catch (err) {
      console.error("PDF export error:", err);
      toast.error(FAILED_TO_EXPORT_PDF);
    } finally {
      setExportingPDF(false);
    }
  };

  // Handle DOCX export
  const handleExportDOCX = async () => {
    const el = getExportElement();
    if (!el) {
      toast.error(EXPORT_CONTENT_NOT_FOUND);
      return;
    }
    setExportingDOCX(true);
    toast.info(EXPORTING_TO_DOCX);
    try {
      await exportToDOCX(el, getFileName());
    } catch (err) {
      console.error("DOCX export error:", err);
      toast.error(FAILED_TO_EXPORT_DOCX);
    } finally {
      setExportingDOCX(false);
    }
  };

  return (
    <>
      {(!isLoading || !isLastMessage) && (
        <div className="feedback-container">
          <div className="feedback-buttons">
            {show.map((buttonType) => {
              switch (buttonType) {
                case "copy":
                  return shouldShowCopy ? (
                    <GenericTooltip key="copy" tooltipText={COPY} tooltipPosition="top">
                      <button
                        className="feedback-button"
                        disabled={copied}
                        onClick={handleCopyClick}
                      >
                        <img
                          src={copied ? copyActive : copyInactive}
                          className="like-dislike-img"
                          alt="copy icon"
                        />
                      </button>
                    </GenericTooltip>
                  ) : null;

                case "audio":
                  return shouldShowAudioOutside ? (
                    <GenericTooltip key="audio" tooltipText={currentAudioId === pair?.id ? STOP_AUDIO : PLAY_AUDIO} tooltipPosition="top">
                      <button
                        className={`feedback-button ${audioLoadingId === pair?.id ? "disabled" : ""}`}
                        disabled={audioLoadingId === pair?.id}
                        onClick={() => handlePlayAudio(pair)}
                      >
                        <img
                          src={currentAudioId === pair?.id ? pauseIcon : speakerIcon}
                          className="like-dislike-img"
                          alt={currentAudioId === pair?.id ? STOP_AUDIO : PLAY_AUDIO}
                        />
                      </button>
                    </GenericTooltip>
                  ) : null;

                case "like":
                  return shouldShowLike ? (
                    <GenericTooltip key="like" tooltipText={LIKE} tooltipPosition="top">
                      <button
                        className={`feedback-button ${feedback === "like" ? "disabled" : ""}`}
                        disabled={feedback === "like"}
                        onClick={() => handleFeedbackClick("like")}
                      >
                        <img
                          src={feedback === "like" ? likeSelected : like}
                          className="like-dislike-img"
                          alt="like icon"
                        />
                      </button>
                    </GenericTooltip>
                  ) : null;

                case "dislike":
                  return shouldShowDislike ? (
                    <GenericTooltip key="dislike" tooltipText={DISLIKE} tooltipPosition="top">
                      <button
                        className={`feedback-button ${feedback === "dislike" ? "disabled" : ""}`}
                        disabled={feedback === "dislike"}
                        onClick={() => handleFeedbackClick("dislike")}
                      >
                        <img
                          src={feedback === "dislike" ? dislikeSelected : dislike}
                          className="like-dislike-img"
                          alt="dislike icon"
                        />
                      </button>
                    </GenericTooltip>
                  ) : null;

                case "regenerate":
                  return (shouldShowRegenerate && isLastMessage) ? (
                    <div key="regenerate" className="generate-container" ref={generateDropdownRef}>
                      <GenericTooltip tooltipText={REGENERATE} tooltipPosition="top">
                        <button
                          ref={triggerGenerateRef}
                          className="feedback-button"
                          onClick={openSelectRegenerationModal}
                        >
                          <img
                            src={isRegenerateOptionModalOpen ? activeRegenerateIcon : regenerateIcon}
                            className="like-dislike-img"
                            alt="Regenerate icon"
                          />
                        </button>
                      </GenericTooltip>
                      {isRegenerateOptionModalOpen && (
                        <div
                          className={`generate-dropdown ${generateModalPosition === "top" ? "generation-modal-top" : "modal-bottom"
                            }`}
                          ref={generateModalRef}
                        >
                          <div className="generate-header">{REGENERATE_WITH}</div>
                          <div className="generated-model-content thin-scroll">
                            {models?.map((opt) => {
                              const isGrok = opt?.name?.toLowerCase().includes("grok");
                              const isDisabled = isGrok && thinkingEnabled;

                              return (
                                <div
                                  key={opt.name}
                                  onClick={() => !isDisabled && handleOutputModalGenerationSelect(opt)}
                                  className={`generate-item ${isDisabled ? "disabled" : ""}`}
                                >
                                  <div className="generate-label">
                                    <span className="name">{opt.name}</span>
                                    <span className="description">{opt.label}</span>
                                  </div>
                                  {opt.isSelected && (
                                    <div className="selected-icon">
                                      <img src={selectedIcon} alt="selected-icon" />
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : null;

                case "download":
                  return (shouldShowDownload && (imageData?.base64_image || procurementTableData?.length > 0)) ? (
                    <GenericTooltip key="download" tooltipText={DOWNLOAD_IMAGE} tooltipPosition="top">
                      <button
                        className="feedback-button"
                        onClick={handleDownload}
                      >
                        <img
                          src={downloadIcon}
                          className="like-dislike-img"
                          alt={DOWNLOAD_IMAGE}
                        />
                      </button>
                    </GenericTooltip>
                  ) : null;

                default:
                  return null;
              }
            })}

            {/* Kebab Menu for Play Audio and Exports */}
            {shouldShowKebabMenu && (
              <div className="more-menu-container" ref={menuDropdownRef}>
                <GenericTooltip tooltipText={MORE_OPTIONS} tooltipPosition="top">
                  <button
                    ref={menuTriggerRef}
                    className="feedback-button"
                    onClick={openMenuModal}
                  >
                    <img
                      src={threeDotsIcon}
                      className="like-dislike-img kebab-icon"
                      alt={MORE_OPTIONS}
                    />
                  </button>
                </GenericTooltip>

                {isMenuOpen && (
                  <div className={`more-menu-dropdown ${menuPosition === "top" ? "menu-top" : "menu-bottom"}`}>
                    {shouldShowAudioInside && (
                      <div
                        className={`menu-item ${audioLoadingId === pair?.id ? "disabled" : ""}`}
                        onClick={() => {
                          if (audioLoadingId !== pair?.id) {
                            handlePlayAudio(pair);
                            setIsMenuOpen(false);
                          }
                        }}
                      >
                        <img
                          src={currentAudioId === pair?.id ? pauseIcon : speakerIcon}
                          alt={currentAudioId === pair?.id ? STOP_AUDIO : PLAY_AUDIO}
                        />
                        <span>{currentAudioId === pair?.id ? STOP_AUDIO : PLAY_AUDIO}</span>
                      </div>
                    )}

                    {shouldShowExportPDF && (
                      <div
                        className={`menu-item ${exportingPDF ? "disabled" : ""}`}
                        onClick={() => {
                          if (!exportingPDF) {
                            handleExportPDF();
                            setIsMenuOpen(false);
                          }
                        }}
                      >
                        {exportingPDF ? (
                          <span className="export-spinner" />
                        ) : (
                          <img src={pdfIcon} alt={EXPORT_AS_PDF} />
                        )}
                        <span>{EXPORT_AS_PDF}</span>
                      </div>
                    )}

                    {shouldShowExportDOCX && (
                      <div
                        className={`menu-item ${exportingDOCX ? "disabled" : ""}`}
                        onClick={() => {
                          if (!exportingDOCX) {
                            handleExportDOCX();
                            setIsMenuOpen(false);
                          }
                        }}
                      >
                        {exportingDOCX ? (
                          <span className="export-spinner" />
                        ) : (
                          <img src={wordIcon} alt={EXPORT_AS_DOCX} />
                        )}
                        <span>{EXPORT_AS_DOCX}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {shouldShowDownload && (imageData?.base64_image || procurementTableData?.length > 0) && (
              <GenericTooltip tooltipText={DOWNLOAD_IMAGE} tooltipPosition="top">
                <button
                  className="feedback-button"
                  onClick={handleDownload}
                >
                  <img
                    src={downloadIcon}
                    className="like-dislike-img"
                    alt={DOWNLOAD_IMAGE}
                  />
                </button>
              </GenericTooltip>
            )}

          </div>

          {/* Feedback Input */}
          {showFeedbackInput && shouldShowDislike && (
            <div ref={feedbackContainerRef} className="additional-feedback">
              <button className="close-arrow" onClick={handleFeedbackModalClose}>
                <img src={closeIcon} alt="arrow-icon" />
              </button>
              <div className="feedback-input-area">
                <textarea
                  ref={feedbackInputRef}
                  className="feedback-input"
                  value={feedbackInput}
                  onChange={handleFeedbackCommentChange}
                  placeholder={YOUR_FEEDBACK}
                  maxLength={5000}
                  onKeyDown={handleKeyPressFeedback}
                  style={{ resize: "none" }}
                />
                {isTypingFeedback && (
                  <div className="apply-feedback">{PRESS_ENTER_TO_SUBMIT}</div>
                )}
              </div>
            </div>
          )}

        </div>
      )}
    </>
  );
};

export default FeedbackButtons;

